/**
 * Scandals Service
 *
 * Provides functions for fetching and managing scandals.
 * Used by PR actions for damage control targeting.
 *
 * @module services/scandals
 */

import { supabase } from '@/lib/supabase'
import type { Scandal, ScandalWithParty } from '@/types'
import { PR_EFFECTS } from '@/constants/pr'

// ============================================================================
// Query Keys
// ============================================================================

export const scandalKeys = {
  all: ['scandals'] as const,
  byParty: (partyId: string) => [...scandalKeys.all, 'party', partyId] as const,
  active: (partyId: string, currentTick: number) =>
    [...scandalKeys.byParty(partyId), 'active', currentTick] as const,
}

// ============================================================================
// Fetch Functions
// ============================================================================

/**
 * Fetch active scandals affecting a party.
 *
 * Returns scandals that:
 * - Target the specified party
 * - Are still active (not resolved)
 * - Were discovered within the lookback window
 *
 * @param partyId - The party ID to fetch scandals for
 * @param currentTick - Current game tick for filtering
 * @returns Array of active scandals with party data
 */
export async function fetchActiveScandals(
  partyId: string,
  currentTick: number
): Promise<ScandalWithParty[]> {
  const minTick = currentTick - PR_EFFECTS.SCANDAL_LOOKBACK_TICKS

  try {
    const { data, error } = await supabase
      .from('scandals')
      .select(`
        *,
        target_party:parties!scandals_target_party_id_fkey (
          id,
          name,
          abbreviation,
          color
        ),
        source_party:parties!scandals_source_party_id_fkey (
          id,
          name,
          abbreviation,
          color
        )
      `)
      .eq('target_party_id', partyId)
      .in('status', ['active', 'fading'])
      .gte('discovered_tick', minTick)
      .order('discovered_tick', { ascending: false })

    if (error) {
      // Table may not exist yet - return empty array
      if (error.code === '42P01' || error.message.includes('does not exist')) {
        console.warn('Scandals table does not exist yet. Returning empty array.')
        return []
      }
      throw error
    }

    return (data as ScandalWithParty[]) || []
  } catch (err) {
    // Gracefully handle table not existing
    console.warn('Error fetching scandals:', err)
    return []
  }
}

/**
 * Fetch all scandals targeting a party (for history/analytics).
 *
 * @param partyId - The party ID to fetch scandals for
 * @returns Array of all scandals targeting the party
 */
export async function fetchScandalsByParty(partyId: string): Promise<Scandal[]> {
  try {
    const { data, error } = await supabase
      .from('scandals')
      .select('*')
      .eq('target_party_id', partyId)
      .order('discovered_tick', { ascending: false })

    if (error) {
      if (error.code === '42P01' || error.message.includes('does not exist')) {
        return []
      }
      throw error
    }

    return (data as Scandal[]) || []
  } catch (err) {
    console.warn('Error fetching scandals by party:', err)
    return []
  }
}

/**
 * Check if a scandal has been recently addressed by PR.
 *
 * Looks for resolved PR damage_control actions targeting this scandal
 * within the cooldown period.
 *
 * @param scandalId - The scandal ID to check
 * @param partyId - The party that would be doing the PR
 * @param currentTick - Current game tick
 * @returns True if scandal was recently addressed
 */
export async function wasScandalRecentlyAddressed(
  _scandalId: string, // Prefixed with underscore - will be used when parameters are properly typed
  partyId: string,
  currentTick: number
): Promise<boolean> {
  const cooldownStart = currentTick - PR_EFFECTS.DAMAGE_CONTROL_COOLDOWN_TICKS

  try {
    const { data, error } = await supabase
      .from('party_actions')
      .select('id')
      .eq('party_id', partyId)
      .eq('action_type', 'pr')
      .eq('action_subtype', 'damage_control')
      .gte('tick', cooldownStart)
      .limit(1)

    if (error) throw error

    // Check if any of these actions targeted this scandal
    if (data && data.length > 0) {
      // TODO: When parameters are properly typed, check for event_id match using _scandalId
      return true
    }

    return false
  } catch (err) {
    console.warn('Error checking scandal address status:', err)
    return false
  }
}

// ============================================================================
// Success Events (Placeholder)
// ============================================================================

/**
 * Placeholder type for success events until events table is implemented.
 */
export interface SuccessEvent {
  id: string
  event_type: 'policy_success' | 'election_victory' | 'economic_improvement'
  name: string
  description: string
  tick: number
  potential_momentum: number
  affected_regions?: string[]
}

/**
 * Fetch recent success events for a party.
 *
 * TODO: This is a placeholder that returns mock data.
 * Will be replaced when events table is implemented.
 *
 * @param partyId - The party ID to fetch successes for
 * @param currentTick - Current game tick for filtering
 * @returns Array of recent success events
 */
export async function fetchRecentSuccesses(
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  _partyId: string,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  _currentTick: number
): Promise<SuccessEvent[]> {
  // TODO: Implement when events table exists
  // For now, return empty array - no successes to highlight
  // This means "Highlight Success" will be disabled in the UI

  // When events table is ready, query like this:
  // const minTick = _currentTick - PR_EFFECTS.SUCCESS_LOOKBACK_TICKS
  // const { data } = await supabase
  //   .from('events')
  //   .select('*')
  //   .contains('beneficiary_parties', [_partyId])
  //   .in('event_type', ['policy_success', 'election_victory', 'economic_improvement'])
  //   .gte('tick', minTick)
  //   .order('tick', { ascending: false })

  console.info('fetchRecentSuccesses: Events table not yet implemented. Returning empty array.')
  return []
}

/**
 * Check if a success event has already been highlighted.
 *
 * @param successEventId - The success event ID to check
 * @param partyId - The party ID
 * @returns True if already highlighted
 */
export async function wasSuccessAlreadyHighlighted(
  _successEventId: string, // Prefixed - will be used when parameters are properly typed
  partyId: string
): Promise<boolean> {
  try {
    const { data, error } = await supabase
      .from('party_actions')
      .select('id')
      .eq('party_id', partyId)
      .eq('action_type', 'pr')
      .eq('action_subtype', 'highlight_success')
      .limit(1)

    if (error) throw error

    // TODO: Check parameters.success_event_id using _successEventId when properly typed
    return (data?.length ?? 0) > 0
  } catch (err) {
    console.warn('Error checking success highlight status:', err)
    return false
  }
}
